import React, { useMemo } from 'react';
import { Shield, Info, AlertTriangle } from 'lucide-react';
import { CalculatedMetrics } from '../../types';

interface RiskAnalyticsProps {
  data: CalculatedMetrics[];
}

interface RiskMetrics {
  valueAtRisk95: number;
  valueAtRisk99: number;
  conditionalVaR: number;
  maxDrawdown: number;
  sharpeRatio: number;
  sortinoRatio: number;
  winLossRatio: number;
  profitFactor: number;
}

interface RiskLevel {
  label: string;
  value: number;
  status: 'low' | 'medium' | 'high';
  color: string;
}

export const RiskAnalytics: React.FC<RiskAnalyticsProps> = ({ data }) => {
  const riskMetrics = useMemo((): RiskMetrics => {
    if (data.length === 0) {
      return {
        valueAtRisk95: 0,
        valueAtRisk99: 0,
        conditionalVaR: 0,
        maxDrawdown: 0,
        sharpeRatio: 0,
        sortinoRatio: 0,
        winLossRatio: 0,
        profitFactor: 0
      };
    }

    const returns = data.map(d => {
      const ret = ((d.Close - d.High) / d.High) * 100;
      return isFinite(ret) ? ret : 0;
    }).sort((a, b) => a - b);

    const var95Index = Math.floor(returns.length * 0.05);
    const var99Index = Math.floor(returns.length * 0.01);
    const valueAtRisk95 = Math.abs(returns[var95Index] || 0);
    const valueAtRisk99 = Math.abs(returns[var99Index] || 0);

    const tailLosses = returns.slice(0, var95Index);
    const conditionalVaR = tailLosses.length > 0
      ? Math.abs(tailLosses.reduce((sum, r) => sum + r, 0) / tailLosses.length)
      : 0;

    let runningSum = 0;
    let peak = 0;
    let maxDrawdown = 0;
    returns.forEach(ret => {
      runningSum += ret;
      if (runningSum > peak) {
        peak = runningSum;
      }
      const drawdown = peak - runningSum;
      if (drawdown > maxDrawdown) {
        maxDrawdown = drawdown;
      }
    });

    const meanReturn = returns.reduce((sum, r) => sum + r, 0) / returns.length;
    const variance = returns.reduce((sum, r) => sum + Math.pow(r - meanReturn, 2), 0) / returns.length;
    const stdDev = Math.sqrt(variance);

    const riskFreeRate = 0;
    const sharpeRatio = stdDev !== 0 ? (meanReturn - riskFreeRate) / stdDev : 0;

    const downsideReturns = returns.filter(r => r < 0);
    const downsideVariance = downsideReturns.length > 0
      ? downsideReturns.reduce((sum, r) => sum + Math.pow(r, 2), 0) / downsideReturns.length
      : 0;
    const downsideDeviation = Math.sqrt(downsideVariance);
    const sortinoRatio = downsideDeviation !== 0 ? meanReturn / downsideDeviation : 0;

    const wins = returns.filter(r => r < 0);
    const losses = returns.filter(r => r > 0);
    const avgWin = wins.length > 0 ? Math.abs(wins.reduce((sum, r) => sum + r, 0) / wins.length) : 0;
    const avgLoss = losses.length > 0 ? Math.abs(losses.reduce((sum, r) => sum + r, 0) / losses.length) : 0;
    const winLossRatio = avgLoss !== 0 ? avgWin / avgLoss : 0;

    const totalWins = Math.abs(wins.reduce((sum, r) => sum + r, 0));
    const totalLosses = Math.abs(losses.reduce((sum, r) => sum + r, 0));
    const profitFactor = totalLosses !== 0 ? totalWins / totalLosses : 0;

    return {
      valueAtRisk95,
      valueAtRisk99,
      conditionalVaR,
      maxDrawdown,
      sharpeRatio,
      sortinoRatio,
      winLossRatio,
      profitFactor
    };
  }, [data]);

  const riskLevels = useMemo((): RiskLevel[] => {
    const getStatus = (metric: string, value: number): { status: 'low' | 'medium' | 'high'; color: string } => {
      switch (metric) {
        case 'var':
          if (value < 5) return { status: 'low', color: '#10B981' };
          if (value < 10) return { status: 'medium', color: '#F59E0B' };
          return { status: 'high', color: '#EF4444' };
        case 'drawdown':
          if (value < 10) return { status: 'low', color: '#10B981' };
          if (value < 20) return { status: 'medium', color: '#F59E0B' };
          return { status: 'high', color: '#EF4444' };
        case 'sharpe':
          if (value > 1) return { status: 'low', color: '#10B981' };
          if (value > 0.5) return { status: 'medium', color: '#F59E0B' };
          return { status: 'high', color: '#EF4444' };
        default:
          return { status: 'medium', color: '#F59E0B' };
      }
    };

    return [
      {
        label: 'VaR 95%',
        value: riskMetrics.valueAtRisk95,
        ...getStatus('var', riskMetrics.valueAtRisk95)
      },
      {
        label: 'VaR 99%',
        value: riskMetrics.valueAtRisk99,
        ...getStatus('var', riskMetrics.valueAtRisk99)
      },
      {
        label: 'CVaR',
        value: riskMetrics.conditionalVaR,
        ...getStatus('var', riskMetrics.conditionalVaR)
      },
      {
        label: 'Max Drawdown',
        value: riskMetrics.maxDrawdown,
        ...getStatus('drawdown', riskMetrics.maxDrawdown)
      }
    ];
  }, [riskMetrics]);

  return (
    <div className="bg-gray-900/30 border border-gray-600 rounded-lg p-4 sm:p-6">
      <div className="flex items-center space-x-2 sm:space-x-3 mb-6">
        <div className="p-1.5 sm:p-2 bg-red-400/10 rounded-lg">
          <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-red-400" />
        </div>
        <div>
          <h4 className="text-base sm:text-lg font-semibold text-white">Risk Analytics</h4>
          <p className="text-xs sm:text-sm text-gray-400">
            Metriche avanzate di rischio e gestione del portafoglio
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="space-y-4">
          <h5 className="text-sm font-medium text-white">Metriche di Rischio (VaR)</h5>
          {riskLevels.map((level, index) => (
            <div key={index} className="bg-gray-800/50 border border-gray-600 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-white">{level.label}</span>
                <span className="text-2xl font-bold" style={{ color: level.color }}>
                  {level.value.toFixed(2)}%
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${Math.min(level.value * 5, 100)}%`,
                      backgroundColor: level.color
                    }}
                  />
                </div>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    level.status === 'low'
                      ? 'bg-green-400/20 text-green-400'
                      : level.status === 'medium'
                      ? 'bg-yellow-400/20 text-yellow-400'
                      : 'bg-red-400/20 text-red-400'
                  }`}
                >
                  {level.status === 'low' ? 'Basso' : level.status === 'medium' ? 'Medio' : 'Alto'}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-4">
          <h5 className="text-sm font-medium text-white">Rapporti di Performance Aggiustati per il Rischio</h5>

          <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-sm font-medium text-white">Sharpe Ratio</p>
                <p className="text-xs text-gray-400">Rendimento per unità di rischio</p>
              </div>
              <p className={`text-2xl font-bold ${
                riskMetrics.sharpeRatio > 1
                  ? 'text-green-400'
                  : riskMetrics.sharpeRatio > 0.5
                  ? 'text-yellow-400'
                  : 'text-red-400'
              }`}>
                {riskMetrics.sharpeRatio.toFixed(2)}
              </p>
            </div>
            <p className="text-xs text-gray-500">
              {riskMetrics.sharpeRatio > 1
                ? 'Eccellente: Rendimenti molto buoni per il rischio assunto'
                : riskMetrics.sharpeRatio > 0.5
                ? 'Buono: Rendimenti accettabili per il rischio'
                : 'Scarso: Rendimenti bassi rispetto al rischio'}
            </p>
          </div>

          <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-sm font-medium text-white">Sortino Ratio</p>
                <p className="text-xs text-gray-400">Focus su downside risk</p>
              </div>
              <p className={`text-2xl font-bold ${
                riskMetrics.sortinoRatio > 1
                  ? 'text-green-400'
                  : riskMetrics.sortinoRatio > 0.5
                  ? 'text-yellow-400'
                  : 'text-red-400'
              }`}>
                {riskMetrics.sortinoRatio.toFixed(2)}
              </p>
            </div>
            <p className="text-xs text-gray-500">
              Considera solo la volatilità negativa (perdite)
            </p>
          </div>

          <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-sm font-medium text-white">Win/Loss Ratio</p>
                <p className="text-xs text-gray-400">Rapporto vittorie/perdite</p>
              </div>
              <p className={`text-2xl font-bold ${
                riskMetrics.winLossRatio > 2
                  ? 'text-green-400'
                  : riskMetrics.winLossRatio > 1
                  ? 'text-yellow-400'
                  : 'text-red-400'
              }`}>
                {riskMetrics.winLossRatio.toFixed(2)}
              </p>
            </div>
            <p className="text-xs text-gray-500">
              Media delle vincite ÷ media delle perdite
            </p>
          </div>

          <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-sm font-medium text-white">Profit Factor</p>
                <p className="text-xs text-gray-400">Profitti totali/Perdite totali</p>
              </div>
              <p className={`text-2xl font-bold ${
                riskMetrics.profitFactor > 2
                  ? 'text-green-400'
                  : riskMetrics.profitFactor > 1
                  ? 'text-yellow-400'
                  : 'text-red-400'
              }`}>
                {riskMetrics.profitFactor.toFixed(2)}
              </p>
            </div>
            <p className="text-xs text-gray-500">
              {riskMetrics.profitFactor > 1
                ? `Ogni $1 perso genera $${riskMetrics.profitFactor.toFixed(2)} di profitto`
                : 'Strategia non profittevole'}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-yellow-400/10 border border-yellow-400/30 rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <AlertTriangle className="h-4 w-4 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <h5 className="text-sm font-medium text-yellow-400">Gestione del Rischio</h5>
              <ul className="text-xs text-yellow-300 mt-2 space-y-1">
                <li>• Limita la posizione al 1-2% del capitale per trade</li>
                <li>• Usa stop loss basati sul VaR 95% o 99%</li>
                <li>• Evita di concentrare più del 5% del capitale in spike simili</li>
                <li>• Considera il Max Drawdown per il dimensionamento del portafoglio</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-blue-400/10 border border-blue-400/30 rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <Info className="h-4 w-4 text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <h5 className="text-sm font-medium text-blue-400">Interpretazione Metriche</h5>
              <ul className="text-xs text-blue-300 mt-2 space-y-1">
                <li>• <strong>VaR 95%:</strong> Perdita massima attesa nel 95% dei casi</li>
                <li>• <strong>CVaR:</strong> Perdita media quando si supera il VaR (tail risk)</li>
                <li>• <strong>Sharpe {'>'} 1:</strong> Strategia eccellente</li>
                <li>• <strong>Profit Factor {'>'} 2:</strong> Strategia molto profittevole</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
